import React from 'react';
import { Boxes } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { humanReadableSchedule } from '@cotera/client/app/v1/trigger/schedule-utils';
import type {
  WorkflowNodeRenderStrategy,
  WorkflowNodeViewData,
} from '../types';
import { asRecord } from '../../utils';

const { WorkflowNodeType, parseBucketConfig } = PersistedWorkflow;

/** "every 500 items" / "on Daily at 9:00 AM". */
function describeTrigger(
  trigger: PersistedWorkflow.BucketFlushTrigger
): string {
  if (trigger.kind === 'count') {
    return `every ${trigger.threshold} item${
      trigger.threshold === 1 ? '' : 's'
    }`;
  }
  return `on ${humanReadableSchedule(trigger.cron)}`;
}

/** One-line summary like `Flush every 500 items or every 1 day`. */
function summarize(node: WorkflowNodeViewData): string | undefined {
  const parsed = parseBucketConfig(asRecord(node.data));
  if (!parsed.ok) {
    return undefined;
  }
  const joiner = parsed.config.policy.combinator === 'and' ? ' and ' : ' or ';
  return `Flush ${parsed.config.policy.triggers
    .map(describeTrigger)
    .join(joiner)}`;
}

const BucketNodeBody: React.FC<{ node: WorkflowNodeViewData }> = ({ node }) => {
  const summary = summarize(node);
  return (
    <Text.Caption className="text-muted-foreground block truncate">
      {summary ?? 'No flush policy configured'}
    </Text.Caption>
  );
};

export const bucketNodeStrategy: WorkflowNodeRenderStrategy = {
  nodeType: WorkflowNodeType.Bucket,
  accent: 'control',
  badge: 'Bucket',
  getTitle: () => 'Bucket',
  getDescription: () =>
    'Accumulate results across runs, then flush downstream once',
  renderIcon: () => <Boxes className="h-4 w-4" />,
  renderBody: (node) => <BucketNodeBody node={node} />,
};
